/**
 * The ONE signup destination (APP-ENTITLEMENT-001 Phase 11): where every "Start your free trial" on every Colony
 * marketing site sends a visitor, and what it tells the wizard about where they came from.
 *
 * The wizard lives on ParcelPuffin's marketing site (`www.parcelpuffin.com/signup/wizard`); a satellite site hands it
 * the APP the visitor came for (`?app=planner`), and an anchor's own site hands it the anchor (`?anchor=publish`),
 * so the anchor question is asked only when nothing is known (owner ruling Q-AE-43). Two spellings of this URL —
 * one per site, one per button — is how a visitor lands on a step the wizard cannot read; so every button on
 * every site builds its href here.
 *
 * `useWizardRouting` (pos-web) reads the same two parameter names from this registry.
 *
 * @module @puffin-core/marketing-shell/registries/signupUrl
 */

import { isAnchorKey, isAppKey } from '@puffin-core/app-catalog';
import { withQuery } from '@puffin-core/route-match';

import { findEcosystemApp } from './ecosystemApps.js';

/** The app whose marketing site hosts the signup wizard. */
export const SIGNUP_SITE_APP_KEY = 'parcel';

/** The wizard's path on that site — pinned equal to pos-web's `POS_MARKETING_ROUTES.SIGNUP_WIZARD`. */
export const SIGNUP_WIZARD_PATH = '/signup/wizard';

/** The query parameters the wizard reads for a satellite entry. */
export const SIGNUP_ENTRY_PARAM = Object.freeze({
  ANCHOR: 'anchor',
  APP: 'app',
});

/** The origin the wizard is served from. */
export const SIGNUP_ORIGIN = findEcosystemApp(SIGNUP_SITE_APP_KEY).marketingUrl;

/**
 * The signup wizard's URL for a visitor who came for an app, an anchor, or nothing in particular.
 *
 * @param {Object} [entry]
 * @param {string} [entry.anchor] - the anchor the visitor chose or the site sells (`parcel`, `publish`, `pro`).
 * @param {string} [entry.app] - the app the visitor came for. An ANCHOR named here is read as the anchor: an
 *   anchor's own site sends its visitors to its own anchor, never to a question about it.
 * @param {string} [entry.fromOrigin] - the origin of the site building the link. On the wizard's own origin the
 *   href is RELATIVE (a router link, no page load); on every other site it is absolute.
 * @returns {string}
 * @throws {TypeError} on a key the ONE code catalog does not know — a stale link is written once, here, not shipped.
 */
export function signupUrlFor({ anchor, app, fromOrigin } = {}) {
  if (anchor !== undefined && !isAnchorKey(anchor)) {
    throw new TypeError(
      `signupUrlFor: anchor must be an anchor key, got ${JSON.stringify(anchor)}`,
    );
  }
  if (app !== undefined && !isAppKey(app)) {
    throw new TypeError(`signupUrlFor: app must be a catalog app key, got ${JSON.stringify(app)}`);
  }
  const anchorKey = anchor ?? (app !== undefined && isAnchorKey(app) ? app : undefined);
  const appKey = app !== undefined && isAnchorKey(app) ? undefined : app;
  const path = withQuery(SIGNUP_WIZARD_PATH, {
    [SIGNUP_ENTRY_PARAM.ANCHOR]: anchorKey,
    [SIGNUP_ENTRY_PARAM.APP]: appKey,
  });
  return fromOrigin === SIGNUP_ORIGIN ? path : `${SIGNUP_ORIGIN}${path}`;
}
